import { isPlacedOrder, messageFromRecord, orderFromRecord } from './adapters/order.ts';
import type { DeliveryLocation, DeliveryQuote, OrdersPort, PaymentStep, ReceiptCheck, RiderPort, RiderView } from './ports.ts';
import { postToWorker, type WorkerClient } from './worker.ts';

type Row = { id: string; data: Record<string, unknown> };
type WatchQuery = (
  collection: string,
  options: { filters?: [string, '==', unknown][]; orderBy?: [string, 'asc' | 'desc']; limit?: number },
  onData: (rows: Row[]) => void,
  onError: (error: Error) => void,
) => () => void;
type WatchDocument = (path: string, onData: (data: Record<string, unknown> | null) => void, onError: (error: Error) => void) => () => void;

// The Worker reports a payment as one of Paystack's charge states; the app
// only needs to know what to show next.
function paymentStep(result: Record<string, unknown>): PaymentStep {
  const status = String(result.status || 'pending');
  const displayText = String(result.displayText || '');
  if (status === 'paid' || status === 'success') return { status: 'paid', orderIds: Array.isArray(result.orderIds) ? result.orderIds.map(String) : [] };
  if (status === 'send_otp') return { status: 'send_otp', displayText };
  if (status === 'redirect' && typeof result.checkoutUrl === 'string') return { status: 'redirect', checkoutUrl: result.checkoutUrl };
  if (status === 'failed') return { status: 'failed', message: String(result.message || 'The payment was declined. No money was taken.') };
  return { status: status === 'pay_offline' ? 'pay_offline' : 'pending', displayText };
}

function newest(rows: Row[]) {
  return rows.filter(row => isPlacedOrder(row.data)).map(row => orderFromRecord(row.id, row.data))
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

export function createOrders({ worker, readDocument, watchQuery, watchDocument, currentUid, baseUrl, fetchImpl = fetch }: {
  worker: WorkerClient;
  baseUrl: string;
  fetchImpl?: typeof fetch;
  readDocument: (collection: string, id: string) => Promise<Record<string, unknown> | null>;
  watchQuery: WatchQuery;
  watchDocument: WatchDocument;
  currentUid: () => string | null;
}): OrdersPort {
  return {
    checkout: request => worker.call('v2_checkout', request),
    deliveryQuote: async (listingIds, point) => (await worker.call<{ quotes: DeliveryQuote[] }>('delivery_quote', { listingIds, point })).quotes,
    pay: async (checkoutId, method) => paymentStep(await worker.call('v2_pay', { checkoutId, ...method })),
    submitOtp: async (checkoutId, otp) => paymentStep(await worker.call('v2_submit_otp', { checkoutId, otp })),
    paymentStatus: async checkoutId => paymentStep(await worker.call('v2_payment_status', { checkoutId })),
    cancelPayment: async checkoutId => { await worker.call('v2_cancel_payment', { checkoutId }); },

    watchMine(listener, onError) {
      const uid = currentUid();
      if (!uid) {
        listener([]);
        return () => {};
      }
      return watchQuery('orders', { filters: [['buyerId', '==', uid]], orderBy: ['createdAt', 'desc'], limit: 100 }, rows => listener(newest(rows)), onError);
    },
    watchShop: (shopId, listener, onError) =>
      watchQuery('orders', { filters: [['shopId', '==', shopId]], orderBy: ['createdAt', 'desc'], limit: 200 }, rows => listener(newest(rows)), onError),
    watch: (orderId, listener, onError) =>
      watchDocument(`orders/${orderId}`, data => listener(data && isPlacedOrder(data) ? orderFromRecord(orderId, data) : null), onError),
    watchMessages: (orderId, listener, onError) =>
      watchQuery(`orders/${orderId}/messages`, { orderBy: ['createdAt', 'asc'], limit: 300 }, rows => listener(rows.map(row => messageFromRecord(row.id, row.data))), onError),
    watchLocation: (orderId, listener, onError) =>
      watchDocument(`riderLocations/${orderId}`, data => {
        const lat = Number(data?.lat);
        const lng = Number(data?.lng);
        const fresh = data && Date.parse(String(data.expiresAt || '')) > Date.now();
        listener(fresh && Number.isFinite(lat) && Number.isFinite(lng)
          ? { point: { lat, lng }, at: String(data.at || ''), by: String(data.by || '') } satisfies DeliveryLocation
          : null);
      }, onError),

    async deliveryCode(orderId) {
      const secret = await readDocument('orderCompletionCodes', orderId);
      return secret?.code ? String(secret.code) : null;
    },
    act: async (orderId, action, reason) => { await worker.call('v2_order_action', { orderId, action, ...(reason ? { reason } : {}) }); },
    setDeliveryMethod: async (orderId, method, options = {}) => { await worker.call('v2_set_delivery_method', { orderId, method, ...(options.verifiedOnly ? { verifiedOnly: true } : {}) }); },
    confirmCode: async (orderId, code) => { await worker.call('v2_confirm_code', { orderId, code }); },
    cancel: orderId => worker.call('v2_cancel_order', { orderId }),
    dropoff: orderId => worker.call('v2_get_dropoff', { orderId }),
    sendMessage: (orderId, text) => worker.call('send_order_message', { orderId, text }),
    shareLocation: async (orderId, point) => { await worker.call('share_delivery_location', { orderId, point }); },
    createRiderLink: (orderId, riderName) => worker.call('create_rider_link', { orderId, riderName }),
    getRiderLink: async orderId => (await worker.call<{ link: Awaited<ReturnType<OrdersPort['getRiderLink']>> }>('get_rider_link', { orderId })).link,
    revokeRiderLink: async orderId => { await worker.call('revoke_rider_link', { orderId }); },
    receiptLink: async orderId => (await worker.call<{ token: string }>('receipt_link', { orderId })).token,
    checkReceipt: (orderId, token) => postToWorker<ReceiptCheck>(fetchImpl, `${baseUrl}/api/v1/receipts`, { orderId, token }),
  };
}

export function createRider({ baseUrl, fetchImpl = fetch }: { baseUrl: string; fetchImpl?: typeof fetch }): RiderPort {
  const send = <T>(token: string, action: string, extra: Record<string, unknown> = {}) =>
    postToWorker<T>(fetchImpl, `${baseUrl}/api/v1/rider`, { token, action, ...extra });
  return {
    view: token => send<RiderView>(token, 'view'),
    pickup: async (token, code) => { await send(token, 'pickup', { code }); },
    deliver: async (token, code) => { await send(token, 'deliver', { code }); },
    shareLocation: async (token, point) => { await send(token, 'location', { point }); },
    message: (token, text) => send(token, 'message', { text }),
  };
}
